import { Box } from "@mui/material";

import { ShippingAddress, UserInteractionType } from "../../../types";

import { ChevronDownIcon, MOBILE_SCREEN_SIZE } from "@brandclub/common-ui";
import { AnimatePresence, motion } from "framer-motion";
import { StoreAnimatedHeightDiv } from "../../../../StoreComponents/StoreAnimatedHeightDiv";
import { useCheckoutContext } from "../../../CheckoutMachine";
import { buildAddressString, getSortedAddresses } from "../../../utils";
import CheckoutEditShippingForm from "../CheckoutEditShippingForm";
import InfoBox from "../InfoBox";

import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import { useSelector } from "@xstate/react";

interface SelectedAddressProps {
  shippingAddress?: Partial<ShippingAddress>;
  onAddShippingAddress: () => void;
}
const SelectedAddress = (props: SelectedAddressProps) => {
  const { shippingAddress } = props;
  if (!shippingAddress) {
    return (
      <InfoBox
        message="Please add a shipping address to complete this purchase."
        cta="Add new shipping address"
        onClick={props.onAddShippingAddress}
      />
    );
  }
  return (
    <div className="info_box">
      <div className="info_row">
        {shippingAddress?.firstName} {shippingAddress?.lastName}
      </div>

      <div className="info_row address">
        {buildAddressString(shippingAddress)}
      </div>
    </div>
  );
};

const ShippingSection = () => {
  const { actorRef, send } = useCheckoutContext();

  const { open, chevronShow, errorMessage } = useSelector(
    actorRef,
    (state) => state.context.sectionState.shipping
  );

  const toggleOpen = () => {
    if (open) {
      send({ type: "ON_SESSION_CLOSE", data: { sessionToClose: "shipping" } });
    } else {
      send({ type: "ON_SESSION_OPEN", data: { sessionToOpen: "shipping" } });
    }
  };

  const shippingAddress = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.shippingAddress
  );

  const userCheckoutInfo = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.userCheckoutInfo
  );

  const availableAddresses = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
        ?.availableAddresses
  );

  const sortedShippingAddresses = getSortedAddresses(availableAddresses);

  const handleSaveShippingAddress = async (
    address: Partial<ShippingAddress>
  ) => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.ADDRESS_SELECTION,
        interactionData: {
          address1: address.address1 ?? "",
          address2: address.address2 ?? "",
          city: address.city ?? "",
          state: address.state ?? "",
          zipCode: address.zipCode ?? "",
          firstName: address.firstName ?? "",
          lastName: address.lastName ?? "",
        },
      },
    });
  };

  // Uses checkout info if available, otherwise uses the shipping address from the user submit form
  const currentShippingAddress =
    shippingAddress ?? userCheckoutInfo?.shippingAddress;

  return (
    <Box className="section">
      <div
        className="section_head"
        style={{ cursor: "pointer" }}
        onClick={() => {}}
      >
        <div className="title">{"Shipping address"}</div>
        {chevronShow ? (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            onClick={toggleOpen}
          ></ChevronDownIcon>
        ) : null}
      </div>

      <StoreAnimatedHeightDiv>
        <StoreErrorV2
          errorMessage={errorMessage}
          sx={{
            width: "fit-content",
            marginTop: "2.2px",
            marginBottom: "24px",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              marginTop: "0px",
              marginBottom: "15px",
            },
          }}
        />
        <AnimatePresence mode="wait">
          <motion.div
            layout="position"
            key={open ? "view" : "edit"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            {open ? (
              <CheckoutEditShippingForm
                availableAddresses={sortedShippingAddresses}
                handleSaveShippingAddress={handleSaveShippingAddress}
                existingAddress={shippingAddress}
              />
            ) : (
              <SelectedAddress
                shippingAddress={currentShippingAddress}
                onAddShippingAddress={() => {
                  send({
                    type: "ON_SESSION_OPEN",
                    data: { sessionToOpen: "shipping" },
                  });
                }}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </StoreAnimatedHeightDiv>
    </Box>
  );
};

export default ShippingSection;
